import React from "react";
import PropTypes from "prop-types";
import EnrollmentItem from "./EnrollmentItem";
import { ENROLLMENT_STATUS } from "../followUpConstants";
import styles from "./EnrollmentList.module.css";

// Chips de status (na ordem de exibição). "" = Todos.
const STATUS_CHIPS = [
  { value: "", label: "Todos" },
  ...Object.entries(ENROLLMENT_STATUS)
    .filter(([, info]) => info.chip)
    .map(([value, info]) => ({ value, label: info.chipLabel })),
];

const TYPE_FILTERS = [
  { value: "", label: "Todos" },
  { value: "LEAD", label: "Leads" },
  { value: "CUSTOMER", label: "Clientes" },
];

/**
 * Aba "Quem está na fila". Filtros por status (chips) + tipo de contato, lista
 * de EnrollmentItem e paginação "Carregar mais". O fetch e as ações vivem no
 * pai (FollowUp); aqui é apresentação + disparo de callbacks.
 */
export default function EnrollmentList({
  enrollments = [],
  total = 0,
  loading = false,
  loadingMore = false,
  error = false,
  statusFilter = "",
  typeFilter = "",
  stepCountBySequence = {},
  busyId = null,
  onStatusChange,
  onTypeChange,
  onLoadMore,
  onResume,
  onCancel,
  onRetry,
}) {
  const hasMore = enrollments.length < total;

  return (
    <div className={styles.wrap}>
      <div className={styles.filters}>
        <div
          className={styles.chips}
          role="group"
          aria-label="Filtrar por situação"
        >
          {STATUS_CHIPS.map((chip) => (
            <button
              key={chip.value || "all"}
              type="button"
              className={`${styles.chip} ${statusFilter === chip.value ? styles.chipActive : ""}`.trim()}
              onClick={() => onStatusChange(chip.value)}
              aria-pressed={statusFilter === chip.value}
            >
              {chip.label}
            </button>
          ))}
        </div>

        <div
          className={styles.typeFilter}
          role="group"
          aria-label="Filtrar por tipo de contato"
        >
          {TYPE_FILTERS.map((t) => (
            <button
              key={t.value || "all-types"}
              type="button"
              className={`${styles.typeBtn} ${typeFilter === t.value ? styles.typeBtnActive : ""}`.trim()}
              onClick={() => onTypeChange(t.value)}
              aria-pressed={typeFilter === t.value}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <ul className={styles.list} aria-busy="true" aria-label="Carregando fila">
          {[0, 1, 2, 3].map((i) => (
            <li key={i} className={styles.skeleton} aria-hidden="true" />
          ))}
        </ul>
      )}

      {!loading && error && (
        <div className={styles.errorBox} role="alert">
          <span>Não foi possível carregar a fila de contatos.</span>
          <button type="button" className={styles.retryBtn} onClick={onRetry}>
            Tentar novamente
          </button>
        </div>
      )}

      {!loading && !error && enrollments.length === 0 && (
        <div className={styles.empty} role="status">
          <span className={styles.emptyIcon} aria-hidden="true">
            📋
          </span>
          <h3 className={styles.emptyTitle}>Nenhum contato na fila ainda</h3>
          <p className={styles.emptyText}>
            Quando clientes não voltarem por 30 dias ou leads não agendarem, eles
            entrarão automaticamente na fila de mensagens.
          </p>
        </div>
      )}

      {!loading && !error && enrollments.length > 0 && (
        <>
          <ul className={styles.list}>
            {enrollments.map((enr) => (
              <EnrollmentItem
                key={enr.id}
                enrollment={enr}
                totalSteps={
                  enr.sequence?.id != null
                    ? (stepCountBySequence[enr.sequence.id] ?? null)
                    : null
                }
                busy={busyId === enr.id}
                onResume={() => onResume(enr)}
                onCancel={() => onCancel(enr)}
              />
            ))}
          </ul>

          {hasMore && (
            <div className={styles.loadMoreWrap}>
              <button
                type="button"
                className={styles.loadMoreBtn}
                onClick={onLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? "Carregando..." : "Carregar mais"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

EnrollmentList.propTypes = {
  enrollments: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  total: PropTypes.number,
  loading: PropTypes.bool,
  loadingMore: PropTypes.bool,
  error: PropTypes.bool,
  statusFilter: PropTypes.string,
  typeFilter: PropTypes.string,
  /** Mapa nome-da-cadência → total de steps (para os progress dots). */
  stepCountBySequence: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  busyId: PropTypes.string,
  onStatusChange: PropTypes.func.isRequired,
  onTypeChange: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  onResume: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
};
